import configureStore from "./store/configureStore";
import { PubSubContext } from "./App";
import { Provider } from "react-redux";
import type React from "react";
import { useState, createContext } from "react";
import { THEME_LIGHT, THEME_DARK } from "./App.constant";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import ProviderLanguage from "./components/ProviderLanguage";

export const store = configureStore(undefined);

/** The CSS custom-property names one theme maps to. */
export type ThemeVars = {
  rootBackground: string;
  rootTextColor: string;
  /** Only the light theme defines this one. */
  userNameTagComponentBackground?: string;
};

export const themes: Record<string, ThemeVars> = {
  [THEME_LIGHT]: {
    rootBackground: "--surface-neutral",
    rootTextColor: "--primary-text",
    userNameTagComponentBackground: "--light-blue-tag",
  },
  [THEME_DARK]: {
    rootBackground: "--surface-black",
    rootTextColor: "--surface-neutral",
  },
};

/**
 * Everything the Provider below actually supplies. The default value carries only `theme`, so the
 * other three are optional — declaring them here is what lets Header / profileSideBar / PortalDashboard
 * read `themeType` and `setTheme` without asserting an intersection at every use.
 */
export type ThemeContextValue = {
  theme: ThemeVars;
  themes?: Record<string, ThemeVars>;
  themeType?: string;
  setTheme?: React.Dispatch<React.SetStateAction<string>>;
};

export const ThemeContext = createContext<ThemeContextValue>({
  // Erased `!`: `themes` is indexed by theme name, and the light theme is always present.
  theme: themes[THEME_LIGHT]!,
});

export const muiTheme = createTheme({
  typography: {
    fontFamily: "inherit",
  },
  zIndex: {
    modal: 1200,
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          "&:active": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiPopper: {
      defaultProps: {
        style: { zIndex: 1400 },
      },
    },
    MuiPopover: {
      defaultProps: {
        style: { zIndex: 1400 },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: {
          zIndex: "1501 !important",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          "& .MuiTabs-indicator": {
            backgroundColor: "#6BBBE3",
            height: "0.25rem",
            borderRadius: "0.125rem",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          padding: "0",
          minWidth: "auto",
          marginRight: "1.5rem", // 6 * 4px (default spacing unit)
          textTransform: "none",
          color: "var(--primary-text)",
          fontWeight: 400,

          // Selected state styling
          "&.Mui-selected": {
            fontWeight: 600,
            color: "var(--primary-text)",
          },
        },
      },
    },
  },
});

function ChildProviderApp({ children }: Readonly<{ children?: React.ReactNode }>) {
  const [theme, setTheme] = useState(THEME_LIGHT);

  return (
    <ProviderLanguage
      isRTLCallback={(isRTL: boolean) => {
        document.documentElement.dir = isRTL ? "rtl" : "ltr";
      }}
    >
      <ThemeProvider theme={muiTheme}>
        {/* KNOWN BUG, preserved verbatim: the Provider supplies `undefined` while the context's
            default is `{ PubSubHelper }`, so any consumer reading `PubSubHelper` under this Provider
            gets a TypeError. `as never` is the erased stand-in for the previous `as any`. */}
        <PubSubContext.Provider value={undefined as never}>
          <ThemeContext.Provider
            value={{
              // Erased `!`: `theme` is always one of the two keys declared above.
              theme: themes[theme]!,
              setTheme,
              themes,
              themeType: theme,
            }}
          >
            {children}
          </ThemeContext.Provider>
        </PubSubContext.Provider>
      </ThemeProvider>
    </ProviderLanguage>
  );
}

function ProviderApp({ children }: Readonly<{ children?: React.ReactNode }>) {
  return (
    <Provider store={store}>
      <ChildProviderApp>{children}</ChildProviderApp>
    </Provider>
  );
}

export default ProviderApp;
